import { apiClient } from "../client";
const ENDPOINTS = {
    LIST: "/contacts",
    FILTER_OPTIONS: "/contacts/filter-options",
    UPDATE_PORTAL_ACCESS: "/contacts/portal-access",
    UPDATE_ADMIN_ACCESS: "/contacts/admin-access",
    UPDATE_APPROVER_ACCESS: "/contacts/approver-access",
    UPDATE_MANAGER_ACCESS: "/contacts/manager-access",
    RELATIVE_CONTACTS: "/contacts/relative",
    SWITCH_ACCOUNT: "/contacts/switch-account",
    BACK_TO_ACCOUNT: "/contacts/back-to-account",
    MY_BILLING: "/contacts/me/billing",
    GHL_BUSINESSES: "/ghl/businesses",
    GHL_CONTACTS: "/ghl/contacts",
    COMPANY_LIST: "/contacts/type/SURVEYOR/company-list",
};
export const contactsService = {
    getContacts: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.search)
            params.append("search", filters.search);
        if (filters.type)
            params.append("type", filters.type);
        if (filters.companyName)
            params.append("companyName", filters.companyName);
        if (filters.page)
            params.append("page", filters.page.toString());
        // API expects CompanyType and ContactType as direct query params (can combine both)
        if (filters.companyType) {
            params.append("CompanyType", filters.companyType);
        }
        if (filters.contactType) {
            params.append("ContactType", filters.contactType);
        }
        if (filters.tier) {
            params.append("Tier", filters.tier);
        }
        if (filters.filter &&
            filters.filter !== "companyType" &&
            filters.filter !== "contactType") {
            params.append("filter", filters.filter);
        }
        return apiClient.get(`${ENDPOINTS.LIST}?${params.toString()}`);
    },
    getFilterOptions: () => apiClient.get(ENDPOINTS.FILTER_OPTIONS),
    getContact: (id, companyContactsPage, companyContactsPageSize) => {
        const params = new URLSearchParams();
        if (companyContactsPage)
            params.append("companyContactsPage", companyContactsPage.toString());
        if (companyContactsPageSize)
            params.append("companyContactsPageSize", companyContactsPageSize.toString());
        const queryString = params.toString();
        return apiClient.get(`/contacts/${id}${queryString ? `?${queryString}` : ""}`);
    },
    /** Get contacts by GHL custom field value (e.g. Department = "Accounts"). */
    getContactsByFieldValue: (fieldKey, value, companyName) => {
        const params = new URLSearchParams();
        if (companyName)
            params.append("companyName", companyName);
        const query = params.toString();
        return apiClient.get(`/contacts/fieldKey/${encodeURIComponent(fieldKey)}/value/${encodeURIComponent(value)}${query ? `?${query}` : ""}`);
    },
    updateContact: (contactId, data) => apiClient.put(`/contacts/${contactId}`, data),
    updatePortalAccess: (data) => apiClient.post(`${ENDPOINTS.UPDATE_PORTAL_ACCESS}`, data),
    updateAdminAccess: (data) => apiClient.post(`${ENDPOINTS.UPDATE_ADMIN_ACCESS}`, data),
    updateApproverAccess: (data) => apiClient.post(`${ENDPOINTS.UPDATE_APPROVER_ACCESS}`, data),
    updateManagerAccess: (data) => apiClient.post(`${ENDPOINTS.UPDATE_MANAGER_ACCESS}`, data),
    getRelativeContacts: (contactId) => apiClient.get(`${ENDPOINTS.RELATIVE_CONTACTS}/${contactId}`),
    assignRelativeContact: (contactId, relativeContactId) => apiClient.post(`${ENDPOINTS.RELATIVE_CONTACTS}`, {
        contactId,
        relativeContactId,
    }),
    deleteRelativeContact: (id) => apiClient.delete(`${ENDPOINTS.RELATIVE_CONTACTS}/${id}`),
    switchAccount: (contactId) => apiClient.post(`${ENDPOINTS.SWITCH_ACCOUNT}`, {
        contactId,
    }),
    backToAccount: () => apiClient.post(`${ENDPOINTS.BACK_TO_ACCOUNT}`),
    // Notes endpoints
    getContactNotes: (contactId) => apiClient.get(`/contacts/${contactId}/notes`),
    createContactNote: (contactId, data) => apiClient.post(`/contacts/${contactId}/notes`, data),
    updateContactNote: (contactId, noteId, data) => apiClient.put(`/contacts/${contactId}/notes/${noteId}`, data),
    deleteContactNote: (contactId, noteId) => apiClient.delete(`/contacts/${contactId}/notes/${noteId}`),
    // Client billing endpoints (for client portal settings)
    getMyBilling: () => apiClient.get(ENDPOINTS.MY_BILLING),
    updateMyBilling: (data) => apiClient.put(ENDPOINTS.MY_BILLING, data),
    // GHL (GoHighLevel) integration
    getGHLBusinesses: () => apiClient.get(ENDPOINTS.GHL_BUSINESSES),
    getUniqueCompanyNames: () => apiClient.get("/ghl/companies/unique"),
    getCompaniesWithContactIds: () => apiClient.get("/ghl/companies/with-contacts"),
    /** Surveyor company names for form linking (e.g. FormEditor "Link to Company"). */
    getCompanyList: () => apiClient.get(ENDPOINTS.COMPANY_LIST),
    getGhlContacts: (subcontractorGroup, tradeCategory, options) => {
        const params = new URLSearchParams();
        params.append("subcontractorGroup", subcontractorGroup);
        params.append("tradeCategory", tradeCategory);
        return apiClient.get(`${ENDPOINTS.GHL_CONTACTS}?${params.toString()}`, { signal: options?.signal });
    },
    createContactInGHL: async (contactPerson) => {
        // TODO: API endpoint not ready yet - placeholder function
        // This will create a contact in GoHighLevel when the API is ready
        // Expected endpoint: POST /contacts/ghl/create
        // Expected payload: { name, email, phone?, role? }
        // Expected response: { ghlContactId: string, email: string, phone?: string, role?: string }
        // Placeholder implementation - returns mock data for testing
        // When API is ready, this will return actual GHL response with email, phone, role
        return Promise.resolve({
            data: {
                ghlContactId: `ghl-${Date.now()}`,
                email: contactPerson.email ||
                    `${contactPerson.name
                        .toLowerCase()
                        .replace(/\s+/g, ".")}@example.com`,
                phone: contactPerson.phone || "+61 400 000 000",
                role: contactPerson.role || "Contact",
                success: true,
                message: "GHL API endpoint not yet implemented - using mock data",
            },
        });
        // Future implementation will look like:
        // return apiClient.post<{ ghlContactId: string; email: string; phone?: string; role?: string }>("/contacts/ghl/create", contactPerson);
    },
};
